import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  Autocomplete,
} from '@mui/material'
import {
  Save,
  CheckCircle,
  Cancel,
  HelpOutline,
  AssignmentTurnedIn,
} from '@mui/icons-material'
import API from '../../../services/API'
import EmployeeSelector from '../../events/EmployeeSelector'
import { UserContext } from '../../../App'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import moment from 'moment'
import AnDateField from '../../../components/DateTime/DateField'
import ErrorModal from '../../../components/Modals/Error'

function useQuery() {
  const { search } = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

export default function CompleteAuditPage() {
  const [templateOptions, setTemplateOptions] = useState([])
  const [template, setTemplate] = useState(null)
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    completedBy: '',
    responses: [],
    notes: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const user = useContext(UserContext)
  let query = useQuery()

  const [searchParams, setSearchParams] = useSearchParams()

  const removeQueryParam = (key) => {
    searchParams.delete(key)
    setSearchParams(searchParams)
  }

  const templateID = searchParams.get('template')

  const navigate = useNavigate()

  const progress = useMemo(() => {
    if (!form.responses.length) return 0

    const answered = form.responses.filter(
      (r) => r.compliant !== null || r.notApplicable,
    ).length

    return Math.round((answered / form.responses.length) * 100)
  }, [form.responses])
  const submissionNotAllowed =
    progress < 100 ||
    !form?.responses?.length ||
    !form?.date ||
    !form?.completedBy ||
    form?.responses?.some(
      (r) =>
        r.compliant === false &&
        r.notApplicable !== true &&
        r.actionRequired &&
        !r.actionTitle,
    )
  useEffect(() => {
    if (!templateID) {
      API.get('/governance/audit/audit-titles').then((res) => {
        if (res.status === 200) {
          setTemplateOptions(res.data)
          console.log('res.data', res.data)
        }
      })
    } else {
      API.get('/governance/audits/template/' + templateID).then((res) => {
        if (res.status === 200) {
          setTemplate(res.data)
          setForm((prev) => ({
            ...prev,
            responses: res.data.questions.map((q) => ({
              name: q.name,
              guidance: q.guidance,
              compliant: null,
              notApplicable: false,
              comment: '',
              actionRequired: false,
              actionTitle: '',
              actionDescription: '',
              actionDueDate: '',
            })),
          }))
          removeQueryParam('template')
        } else {
          removeQueryParam('template')
        }
      })
    }
    // TODO: fetch template by ID
    // API.get(`/governance/audits/template/${id}`).then((res) => {
    //   const template = res.data
    //   setTemplate(template)
    //   setForm((prev) => ({
    //     ...prev,
    //     responses: template.questions.map((q) => ({
    //       name: q.name,
    //       guidance: q.guidance,
    //       compliant: null,
    //       notApplicable: false,
    //       comment: '',
    //       actionRequired: false,
    //       actionTitle: '',
    //       actionDescription: '',
    //       actionDueDate: '',
    //     })),
    //   }))
    // })
  }, [])

  const updateResponse = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      responses: prev.responses.map((response, i) =>
        i === index ? { ...response, [field]: value } : response,
      ),
    }))
  }

  const nonCompliantCount = form.responses.filter(
    (r) => r.compliant === false && !r.notApplicable,
  ).length

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const payload = {
      date: form.date,
      completedBy: form.completedBy,
      auditDescription: template?._id,
      responses: form.responses,
      notes: form.notes,
      overallCompliant: nonCompliantCount === 0,
    }

    API.post('/governance/audit/submit', payload)
      .then((res) => {
        if (res.status === 200) {
          navigate('/governance/audits?submitSuccess=true')
        } else {
          setSubmitError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setSubmitError(true)
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, width: '100%', mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack gap={1}>
          <Typography variant="title2" fontWeight={900}>
            {template?.title ?? 'Complete Audit'}
          </Typography>
          <Typography color="text.secondary">
            {template?.auditDescription
              ? template.auditDescription
              : 'Complete the checklist and record any actions required.'}
          </Typography>
        </Stack>

        <Box>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={submissionNotAllowed || isSubmitting}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.2,
              fontWeight: 700,
              minWidth: 200,
            }}
          >
            Submit Audit
          </Button>
        </Box>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          {!templateID && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                mb: 3,
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="title3" fontWeight={800}>
                  Select an Audit
                </Typography>
                {templateOptions.length === 0 && (
                  <Alert
                    severity="info"
                    action={
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => navigate('/governance/audit-templates')}
                      >
                        Manage Templates
                      </Button>
                    }
                  >
                    No audit templates are available yet. Create a template
                    first — it defines the checklist this form walks through.
                  </Alert>
                )}
                <Autocomplete
                  options={templateOptions}
                  onChange={(e, newValue) => {
                    setTemplate(newValue)
                    setForm((prev) => ({
                      ...prev,
                      responses: (newValue?.questions ?? []).map((q) => ({
                        name: q.name,
                        guidance: q.guidance,
                        compliant: null,
                        notApplicable: false,
                        comment: '',
                        actionRequired: false,
                        actionTitle: '',
                        actionDescription: '',
                        actionDueDate: '',
                      })),
                    }))
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  getOptionLabel={(o) => o.title}
                  renderOption={(props, o) => {
                    const { key, ...rest } = props
                    return (
                      <Stack
                        key={key}
                        {...rest}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        justifyContent="space-between"
                      >
                        <Typography flex={1}>{o.title}</Typography>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip
                            color="contrast"
                            size="small"
                            variant="outlined"
                            label={o.category}
                          />
                          <Chip
                            color={
                              moment(o.nextDue).isSameOrBefore(moment())
                                ? 'error'
                                : 'success'
                            }
                            size="small"
                            variant="outlined"
                            label={`Due ${moment(o.nextDue).format('Do MMM YYYY')}`}
                          />
                        </Stack>
                      </Stack>
                    )
                  }}
                />
              </Stack>
            </Paper>
          )}
          <Stack spacing={2}>
            {form.responses.map((response, index) => (
              <Card
                key={index}
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: response.notApplicable
                    ? 'divider'
                    : response.compliant === true
                      ? 'success.main'
                      : response.compliant === false
                        ? 'error.main'
                        : 'divider',
                }}
              >
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                          <Box>
                            <Typography fontWeight={800}>
                              {index + 1}. {response.name}
                            </Typography>
                            {response.guidance && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {response.guidance}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                          <ToggleButtonGroup
                            exclusive
                            fullWidth
                            value={
                              response.notApplicable
                                ? 'na'
                                : response.compliant === true
                                  ? 'yes'
                                  : response.compliant === false
                                    ? 'no'
                                    : null
                            }
                            onChange={(e, value) => {
                              if (!value) return

                              updateResponse(
                                index,
                                'notApplicable',
                                value === 'na',
                              )
                              updateResponse(
                                index,
                                'compliant',
                                value === 'na' ? null : value === 'yes',
                              )
                            }}
                          >
                            <ToggleButton value="yes" color="success">
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  lineHeight: 1.2,
                                  gap: 1,
                                }}
                              >
                                <CheckCircle fontSize="small" />
                                Compliant
                              </Box>
                            </ToggleButton>
                            <ToggleButton value="no" color="error">
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  lineHeight: 1.2,
                                  gap: 1,
                                }}
                              >
                                <Cancel fontSize="small" />
                                Non-compliant
                              </Box>
                            </ToggleButton>
                            <ToggleButton value="na">
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  lineHeight: 1.2,
                                  gap: 1,
                                }}
                              >
                                <HelpOutline fontSize="small" />
                                N/A
                              </Box>
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Grid>
                      </Grid>
                    </Box>

                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Comments / evidence"
                      value={response.comment}
                      onChange={(e) =>
                        updateResponse(index, 'comment', e.target.value)
                      }
                    />

                    {response.compliant === false &&
                      !response.notApplicable && (
                        <>
                          <Divider />

                          <Alert severity="warning" sx={{ borderRadius: 3 }}>
                            This item is non-compliant. Consider creating a
                            corrective action.
                          </Alert>

                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!response.actionRequired}
                                onChange={(e) =>
                                  updateResponse(
                                    index,
                                    'actionRequired',
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label="Action required"
                          />

                          {response.actionRequired && (
                            <Box>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={8}>
                                  <TextField
                                    fullWidth
                                    label="Action title"
                                    value={response.actionTitle}
                                    autoComplete="off"
                                    onChange={(e) =>
                                      updateResponse(
                                        index,
                                        'actionTitle',
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <AnDateField
                                    label="Due Date"
                                    fullWidth
                                    date={response.actionDueDate}
                                    onChange={(newDate) =>
                                      updateResponse(
                                        index,
                                        'actionDueDate',
                                        newDate,
                                      )
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label="Action description"
                                    autoComplete="off"
                                    value={response.actionDescription}
                                    onChange={(e) =>
                                      updateResponse(
                                        index,
                                        'actionDescription',
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </Box>
                          )}
                        </>
                      )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack
            spacing={3}
            sx={{
              position: { xs: 'static', lg: 'sticky' },
              top: 24,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                Audit Progress
              </Typography>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 10, borderRadius: 5, mb: 1 }}
              />

              <Typography variant="body2" color="text.secondary">
                {progress}% complete
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`${form.responses.length} items`} />
                <Chip
                  label={`${nonCompliantCount} non-compliant`}
                  color={nonCompliantCount ? 'error' : 'success'}
                />
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
                  Audit Details
                </Typography>

                <TextField
                  fullWidth
                  type="date"
                  label="Audit date"
                  value={form.date}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, date: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <EmployeeSelector
                  setSelfAsDefault
                  fullWidth
                  value={form.completedBy}
                  onChange={(e, newValue) =>
                    setForm((prev) => ({ ...prev, completedBy: newValue }))
                  }
                  label="Completed By"
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label="Audit notes"
                  value={form.notes}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </Stack>
            </Paper>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSubmit}
              disabled={submissionNotAllowed || isSubmitting}
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.2,
                fontWeight: 700,
                minWidth: 200,
              }}
            >
              Submit Audit
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <ErrorModal
        open={submitError}
        onClose={() => setSubmitError(false)}
        title="Unable to Submit"
        message="There was an error submitting your audit. Please try again."
      />
    </Box>
  )
}
